import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import usePageSEO from '../hooks/usePageSEO';
import {
  Users, TrendingUp, DollarSign, ArrowRight, CheckCircle,
  Sun, Moon, Globe, BarChart3, Link2, Zap, Scissors, Instagram,
  Dumbbell, Heart, Smartphone, Shield, Star, ChevronRight
} from 'lucide-react';

const t = (lang, de, en) => (lang === 'de' ? de : en);
const HERO_IMG = 'https://images.unsplash.com/photo-1759134198561-e2041049419c?w=900&q=80&auto=format';
const DION_LOGO = 'https://customer-assets.emergentagent.com/job_dion-hair/artifacts/cm40t14d_DionHairClinic_Logo.svg';

const PartnerLanding = () => {
  const { isDark, toggleTheme } = useTheme();
  const { language, toggleLanguage } = useLanguage();
  const [visibleSections, setVisibleSections] = useState({});
  const sectionRefs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) setVisibleSections((p) => ({ ...p, [e.target.id]: true }));
      }),
      { threshold: 0.1 }
    );
    Object.values(sectionRefs.current).forEach((ref) => ref && observer.observe(ref));
    return () => observer.disconnect();
  }, []);

  const reg = (id) => (el) => { if (el) sectionRefs.current[id] = el; };

  usePageSEO({
    title: t(language, 'Partner-Programm | Empfehle Haartransplantation & verdiene 150€ | Dion Hair Clinic', 'Partner Program | Recommend Hair Transplant & Earn €150 | Dion Hair Clinic'),
    description: t(language, 'Werde Partner der Dion Hair Clinic in Mönchengladbach. Verdiene 150€ pro erfolgreicher Haartransplantation. Eigene Promo-Seite, Live-Dashboard, kein Limit. Ideal für Influencer, Barbershops & Friseure.', 'Become a partner of Dion Hair Clinic in Mönchengladbach. Earn €150 per successful hair transplant. Own promo page, live dashboard, no limit. Ideal for influencers, barbershops & hairdressers.'),
    keywords: t(language, 'Haartransplantation empfehlen, Partner Programm Haarklinik, Provision Haartransplantation, Influencer Haartransplantation, Barbershop Kooperation, Dion Hair Clinic Partner', 'hair transplant referral, partner program hair clinic, hair transplant commission, influencer hair transplant, barbershop cooperation, Dion Hair Clinic partner'),
    canonical: 'https://dionhairclinic.de/partner',
    ogTitle: t(language, 'Verdiene 150€ pro Empfehlung | Dion Hair Clinic Partner-Programm', 'Earn €150 per Referral | Dion Hair Clinic Partner Program'),
    ogDescription: t(language, 'Empfehle Haartransplantationen und verdiene 150€ pro abgeschlossener Behandlung.', 'Recommend hair transplants and earn €150 per completed treatment.'),
    ogUrl: 'https://dionhairclinic.de/partner',
    jsonLd: {
      "@context": "https://schema.org", "@type": "WebPage",
      "name": t(language, "Partner-Programm | Dion Hair Clinic", "Partner Program | Dion Hair Clinic"),
      "url": "https://dionhairclinic.de/partner",
      "publisher": { "@type": "Organization", "name": "Dion Hair Clinic", "url": "https://dionhairclinic.de" },
      "mainEntity": { "@type": "Offer", "name": t(language, "Empfehlungsprogramm Haartransplantation", "Hair Transplant Referral Program"), "price": "150", "priceCurrency": "EUR" }
    },
  });

  const audiences = [
    {
      icon: <Instagram className="w-7 h-7" />,
      title: t(language, 'Content Creator & Influencer', 'Content Creators & Influencers'),
      desc: t(language, 'Teile deinen persönlichen Link in deiner Bio oder Story. Deine Community vertraut dir – nutze das.', 'Share your personal link in your bio or story. Your community trusts you – leverage that.'),
      tag: t(language, 'Ideal ab 1.000 Follower', 'Ideal from 1,000 followers'),
    },
    {
      icon: <Scissors className="w-7 h-7" />,
      title: t(language, 'Barbershops & Friseure', 'Barbershops & Hairdressers'),
      desc: t(language, 'Deine Kunden sprechen dich sowieso auf Haarausfall an. Empfehle die beste Lösung – und verdiene dabei.', 'Your clients already ask you about hair loss. Recommend the best solution – and earn from it.'),
      tag: t(language, 'Perfekter Branchen-Fit', 'Perfect industry fit'),
    },
    {
      icon: <Dumbbell className="w-7 h-7" />,
      title: t(language, 'Fitness & Lifestyle', 'Fitness & Lifestyle'),
      desc: t(language, 'Selbstbewusstsein und Aussehen gehören zusammen. Deine Zielgruppe investiert in sich selbst.', 'Confidence and appearance go hand in hand. Your audience invests in themselves.'),
      tag: t(language, 'Hohe Conversion', 'High conversion'),
    },
    {
      icon: <Heart className="w-7 h-7" />,
      title: t(language, 'Jeder mit Netzwerk', 'Anyone with a Network'),
      desc: t(language, 'Du kennst jemanden der unter Haarausfall leidet? Eine ehrliche Empfehlung reicht.', 'Know someone suffering from hair loss? An honest recommendation is all it takes.'),
      tag: t(language, 'Keine Follower nötig', 'No followers needed'),
    },
  ];

  return (
    <div className={`min-h-screen transition-colors duration-500 ${isDark ? 'bg-[#0a0a0a]' : 'bg-white'}`}>
      {/* Controls */}
      <div className="fixed top-4 right-4 z-50 flex items-center gap-2">
        <button onClick={toggleLanguage} data-testid="partner-lang-toggle" className={`p-2.5 rounded-full backdrop-blur-md transition-all ${isDark ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-black/5 hover:bg-black/10 text-gray-700'}`}><Globe className="w-4 h-4" /></button>
        <button onClick={toggleTheme} data-testid="partner-theme-toggle" className={`p-2.5 rounded-full backdrop-blur-md transition-all ${isDark ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-black/5 hover:bg-black/10 text-gray-700'}`}>{isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}</button>
      </div>

      {/* ===== HERO ===== */}
      <section className="relative min-h-[85dvh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <div className={`absolute inset-0 ${isDark ? 'bg-[#0a0a0a]' : 'bg-white'}`} />
          <div className={`absolute top-0 right-0 w-1/2 h-full hidden lg:block`}>
            <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
            <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/80 to-[#0a0a0a]/20' : 'bg-gradient-to-r from-white via-white/85 to-white/20'}`} />
          </div>
          <div className={`absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full blur-[200px] ${isDark ? 'bg-[#7BA7C2]/6' : 'bg-[#7BA7C2]/4'}`} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 w-full">
          <div className="max-w-2xl">
            <img src={DION_LOGO} alt="Dion Hair Clinic" className={`h-10 sm:h-12 w-auto mb-8 ${isDark ? 'brightness-0 invert' : ''}`} />

            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 ${isDark ? 'bg-[#7BA7C2]/10 border border-[#7BA7C2]/20' : 'bg-[#5A8BA6]/8 border border-[#5A8BA6]/12'}`}>
              <TrendingUp className="w-4 h-4 text-[#7BA7C2]" />
              <span className="text-sm font-semibold tracking-wide text-[#7BA7C2]">
                {t(language, 'PARTNER-PROGRAMM', 'PARTNER PROGRAM')}
              </span>
            </div>

            <h1 className={`text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.1] mb-5 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t(language, 'Verdiene', 'Earn')}
              <br />
              <span className="bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] bg-clip-text text-transparent">
                150€{t(language, ' pro Empfehlung', ' per Referral')}
              </span>
            </h1>

            <p className={`text-base sm:text-lg leading-relaxed mb-8 max-w-xl ${isDark ? 'text-white/55' : 'text-gray-500'}`}>
              {t(language,
                'Empfehle die Dion Hair Clinic und erhalte 150€ für jede erfolgreiche Haartransplantation. Eigene Promo-Seite. Live-Dashboard. Kein Limit.',
                'Recommend Dion Hair Clinic and receive €150 for every successful hair transplant. Own promo page. Live dashboard. No limit.'
              )}
            </p>

            <div className="flex flex-col sm:flex-row items-stretch gap-3 mb-10 max-w-md">
              <Link to="/partner/register" className="flex-1">
                <Button className="w-full group px-6 py-4 h-auto text-sm bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] hover:from-[#5A8BA6] hover:to-[#7BA7C2] text-white shadow-xl shadow-[#7BA7C2]/20 hover:shadow-[#7BA7C2]/30 transition-all duration-300 rounded-xl" data-testid="partner-register-cta">
                  {t(language, 'Jetzt kostenlos starten', 'Start for free now')}
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/partner/login" className="flex-1">
                <Button variant="outline" className={`w-full px-6 py-4 h-auto text-sm rounded-xl transition-all ${isDark ? 'border-white/20 text-white hover:bg-white/10' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-testid="partner-login-cta">
                  {t(language, 'Partner-Login', 'Partner Login')}
                </Button>
              </Link>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-2">
                <Shield className={`w-4 h-4 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
                <span className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{t(language, 'Kostenlos', 'Free')}</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className={`w-4 h-4 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
                <span className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{t(language, '2 Min. Anmeldung', '2 min signup')}</span>
              </div>
              <div className="flex items-center gap-2">
                <DollarSign className={`w-4 h-4 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
                <span className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{t(language, 'Kein Limit', 'No limit')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section id="pw-steps" ref={reg('pw-steps')} className={`py-16 sm:py-20 ${isDark ? 'bg-[#0f0f0f]' : 'bg-gray-50/80'}`}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className={`text-center mb-12 transition-all duration-700 ${visibleSections['pw-steps'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium mb-4 ${isDark ? 'bg-[#7BA7C2]/10 text-[#7BA7C2]' : 'bg-[#5A8BA6]/10 text-[#5A8BA6]'}`}>
              {t(language, '3 einfache Schritte', '3 simple steps')}
            </span>
            <h2 className={`text-3xl sm:text-4xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t(language, 'So einfach geht\'s', 'It\'s that simple')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {[
              { num: '01', icon: <Smartphone className="w-6 h-6" />, title: t(language, 'Registrieren', 'Register'), desc: t(language, 'Erstelle in unter 2 Minuten dein kostenloses Partner-Konto. Keine Kosten, keine Verpflichtungen.', 'Create your free partner account in under 2 minutes. No costs, no obligations.') },
              { num: '02', icon: <Link2 className="w-6 h-6" />, title: t(language, 'Link teilen', 'Share your link'), desc: t(language, 'Du bekommst sofort eine eigene Promo-Seite mit deinem Code. Teile den Link in Bio, Story oder persönlich.', 'You instantly get your own promo page with your code. Share the link in bio, story or personally.') },
              { num: '03', icon: <DollarSign className="w-6 h-6" />, title: t(language, '150€ kassieren', 'Collect €150'), desc: t(language, 'Für jede Haartransplantation die über dich zustande kommt, erhältst du 150€. Transparent getrackt in deinem Dashboard.', 'For every hair transplant that comes through you, you receive €150. Transparently tracked in your dashboard.') },
            ].map((s, i) => (
              <div
                key={i}
                className={`relative rounded-2xl p-7 border transition-all duration-500 group ${
                  isDark ? 'bg-[#111] border-white/5 hover:border-[#7BA7C2]/20' : 'bg-white border-gray-100 hover:border-[#5A8BA6]/20 shadow-sm'
                } ${visibleSections['pw-steps'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ transitionDelay: `${i * 150 + 200}ms` }}
              >
                <div className="flex items-center gap-3 mb-5">
                  <span className="text-3xl font-bold bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] bg-clip-text text-transparent">{s.num}</span>
                  <div className={`p-2.5 rounded-xl ${isDark ? 'bg-[#7BA7C2]/10 text-[#7BA7C2]' : 'bg-[#5A8BA6]/10 text-[#5A8BA6]'}`}>{s.icon}</div>
                </div>
                <h3 className={`text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>{s.title}</h3>
                <p className={`text-sm leading-relaxed ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{s.desc}</p>
                {i < 2 && (
                  <div className="hidden md:block absolute -right-4 top-1/2 -translate-y-1/2 z-10">
                    <ChevronRight className={`w-6 h-6 ${isDark ? 'text-white/10' : 'text-gray-200'}`} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FOR WHOM ===== */}
      <section id="pw-audience" ref={reg('pw-audience')} className={`py-16 sm:py-20 ${isDark ? 'bg-[#0f0f0f]' : 'bg-gray-50/80'}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className={`text-center mb-12 transition-all duration-700 ${visibleSections['pw-audience'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium mb-4 ${isDark ? 'bg-[#7BA7C2]/10 text-[#7BA7C2]' : 'bg-[#5A8BA6]/10 text-[#5A8BA6]'}`}>
              {t(language, 'Für wen?', 'For whom?')}
            </span>
            <h2 className={`text-3xl sm:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t(language, 'Perfekt für dich, wenn...', 'Perfect for you if...')}
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {audiences.map((a, i) => (
              <div
                key={i}
                className={`rounded-2xl p-7 border transition-all duration-500 group hover:scale-[1.01] ${
                  isDark ? 'bg-[#111] border-white/5 hover:border-[#7BA7C2]/20' : 'bg-white border-gray-100 hover:border-[#5A8BA6]/20 shadow-sm'
                } ${visibleSections['pw-audience'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ transitionDelay: `${i * 100 + 200}ms` }}
              >
                <div className={`inline-flex p-3 rounded-2xl mb-5 transition-colors ${isDark ? 'bg-[#7BA7C2]/10 text-[#7BA7C2] group-hover:bg-[#7BA7C2]/15' : 'bg-[#5A8BA6]/10 text-[#5A8BA6] group-hover:bg-[#5A8BA6]/15'}`}>
                  {a.icon}
                </div>
                <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{a.title}</h3>
                <p className={`text-sm leading-relaxed mb-4 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{a.desc}</p>
                <span className={`inline-flex px-3 py-1.5 rounded-full text-xs font-medium ${isDark ? 'bg-[#7BA7C2]/8 text-[#7BA7C2]' : 'bg-[#5A8BA6]/8 text-[#5A8BA6]'}`}>{a.tag}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== WHAT YOU GET ===== */}
      <section id="pw-features" ref={reg('pw-features')} className={`py-16 sm:py-20 ${isDark ? 'bg-[#0a0a0a]' : 'bg-white'}`}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className={`text-center mb-12 transition-all duration-700 ${visibleSections['pw-features'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className={`text-3xl sm:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t(language, 'Was du bekommst', 'What you get')}
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: <Link2 className="w-5 h-5" />, title: t(language, 'Eigene Promo-Seite', 'Own Promo Page'), desc: t(language, 'Professionelle Landingpage mit deinem Namen und Code – sofort einsatzbereit.', 'Professional landing page with your name and code – ready to use immediately.') },
              { icon: <BarChart3 className="w-5 h-5" />, title: t(language, 'Live-Dashboard', 'Live Dashboard'), desc: t(language, 'Tracke Klicks, Anfragen und Behandlungen in Echtzeit. Volle Transparenz.', 'Track clicks, inquiries and treatments in real-time. Full transparency.') },
              { icon: <DollarSign className="w-5 h-5" />, title: t(language, '150€ pro Behandlung', '€150 per Treatment'), desc: t(language, 'Feste Provision für jede abgeschlossene Haartransplantation. Ohne Limit.', 'Fixed commission for every completed hair transplant. No limit.') },
              { icon: <Smartphone className="w-5 h-5" />, title: t(language, 'Mobile optimiert', 'Mobile Optimized'), desc: t(language, 'Deine Promo-Seite sieht auf jedem Gerät perfekt aus.', 'Your promo page looks perfect on every device.') },
              { icon: <Star className="w-5 h-5" />, title: t(language, '5.0 Google Rating', '5.0 Google Rating'), desc: t(language, 'Empfehle eine Klinik mit Top-Bewertungen. Das überzeugt deine Kontakte.', 'Recommend a clinic with top ratings. This convinces your contacts.') },
              { icon: <Shield className="w-5 h-5" />, title: t(language, 'Kein Risiko', 'No Risk'), desc: t(language, 'Komplett kostenlos. Keine Mindestempfehlungen. Jederzeit kündbar.', 'Completely free. No minimum referrals. Cancel anytime.') },
            ].map((f, i) => (
              <div
                key={i}
                className={`flex items-start gap-4 p-5 rounded-xl border transition-all duration-500 ${
                  isDark ? 'bg-[#111] border-white/5' : 'bg-gray-50/50 border-gray-100'
                } ${visibleSections['pw-features'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                style={{ transitionDelay: `${i * 80 + 200}ms` }}
              >
                <div className={`p-2 rounded-lg shrink-0 ${isDark ? 'bg-[#7BA7C2]/10 text-[#7BA7C2]' : 'bg-[#5A8BA6]/10 text-[#5A8BA6]'}`}>{f.icon}</div>
                <div>
                  <h3 className={`text-sm font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{f.title}</h3>
                  <p className={`text-xs leading-relaxed ${isDark ? 'text-white/45' : 'text-gray-500'}`}>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SOCIAL PROOF ===== */}
      <section id="pw-trust" ref={reg('pw-trust')} className={`py-14 ${isDark ? 'bg-[#0f0f0f]' : 'bg-gray-50/80'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className={`rounded-3xl p-8 sm:p-12 border text-center transition-all duration-500 ${
            isDark ? 'bg-[#111] border-white/5' : 'bg-white border-gray-100 shadow-sm'
          } ${visibleSections['pw-trust'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="flex items-center justify-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (<Star key={i} className="w-6 h-6 fill-[#7BA7C2] text-[#7BA7C2]" />))}
            </div>
            <h3 className={`text-2xl sm:text-3xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t(language, '5.0 bei über 140 Google-Bewertungen', '5.0 from over 140 Google reviews')}
            </h3>
            <p className={`max-w-lg mx-auto mb-8 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              {t(language,
                'Die Dion Hair Clinic gehört zu den bestbewerteten Kliniken in Deutschland. Deine Empfehlung steht auf einem starken Fundament.',
                'Dion Hair Clinic is one of the highest-rated clinics in Germany. Your recommendation stands on a strong foundation.'
              )}
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              {[
                { value: '4.000+', label: t(language, 'Behandlungen', 'Treatments') },
                { value: t(language, 'Mönchengladbach', 'Mönchengladbach'), label: t(language, 'Standort', 'Location') },
                { value: 'FUE & DHI', label: t(language, 'Saphir-Technologie', 'Sapphire Technology') },
              ].map((s, i) => (
                <div key={i} className="text-center">
                  <div className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{s.value}</div>
                  <div className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className={`py-16 sm:py-20 ${isDark ? 'bg-[#0a0a0a]' : 'bg-white'}`}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className={`text-2xl sm:text-3xl font-bold mb-5 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t(language, 'Bereit loszulegen?', 'Ready to get started?')}
          </h2>
          <p className={`text-base mb-8 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
            {t(language,
              'Registriere dich jetzt kostenlos und erhalte sofort deinen persönlichen Empfehlungslink.',
              'Register now for free and instantly get your personal referral link.'
            )}
          </p>
          <Link to="/partner/register">
            <Button className="group px-8 py-4 h-auto text-base bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] text-white shadow-xl shadow-[#7BA7C2]/20 hover:shadow-[#7BA7C2]/30 transition-all duration-300 rounded-xl hover:scale-[1.02]" data-testid="partner-register-bottom-cta">
              {t(language, 'Jetzt Partner werden', 'Become a Partner Now')}
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
          <p className={`mt-4 text-sm ${isDark ? 'text-white/30' : 'text-gray-400'}`}>
            {t(language, 'Kostenlos. Unverbindlich. In 2 Minuten startklar.', 'Free. No obligation. Ready in 2 minutes.')}
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className={`py-10 border-t ${isDark ? 'bg-[#0a0a0a] border-white/5' : 'bg-white border-gray-100'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <img src={DION_LOGO} alt="Dion Hair Clinic" className={`h-8 w-auto mx-auto mb-4 ${isDark ? 'brightness-0 invert' : ''}`} />
          <p className={`text-sm mb-2 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Dion Hair Clinic &middot; {t(language, 'Mönchengladbach, Deutschland', 'Mönchengladbach, Germany')}</p>
          <div className="flex items-center justify-center gap-4 text-xs">
            <a href="/impressum" className={isDark ? 'text-white/30 hover:text-white/60' : 'text-gray-400 hover:text-gray-600'}>{t(language, 'Impressum', 'Legal Notice')}</a>
            <span className={isDark ? 'text-white/10' : 'text-gray-200'}>|</span>
            <a href="/datenschutz" className={isDark ? 'text-white/30 hover:text-white/60' : 'text-gray-400 hover:text-gray-600'}>{t(language, 'Datenschutz', 'Privacy')}</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PartnerLanding;
